import { useState, useEffect, useCallback, useRef } from 'react';
import leagueService, { League, LeaguePlayer } from '../services/leagueService';

interface UseLeagueDetailReturn {
  league: League | null;
  players: LeaguePlayer[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useLeagueDetail = (leagueId: number | string): UseLeagueDetailReturn => {
  const [league, setLeague] = useState<League | null>(null);
  const [players, setPlayers] = useState<LeaguePlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchedId = useRef<string | number | null>(null);

  const fetchLeagueDetail = useCallback(async (id: number | string) => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const numericLeagueId = typeof id === 'string' ? parseInt(id, 10) : id;
      
      if (isNaN(numericLeagueId)) {
        throw new Error('ID de liga inválido');
      }
      
      // Fetch league details and players in parallel
      const [leagueData, playersData] = await Promise.all([
        leagueService.getLeague(numericLeagueId),
        leagueService.getLeaguePlayers(numericLeagueId)
      ]);
      
      // Calcular is_active basándose en end_date para consistencia
      const leagueWithCalculatedStatus = {
        ...leagueData,
        is_active: calculateIsActive(leagueData.end_date)
      };
      
      setLeague(leagueWithCalculatedStatus);
      setPlayers(playersData);
      lastFetchedId.current = id;
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching league detail:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Función para calcular si una liga está activa (misma lógica que useLeagues)
  const calculateIsActive = (endDate?: string): boolean => {
    if (!endDate) {
      // Si no hay end_date, la liga está activa
      console.log('Liga detalle sin end_date, marcada como activa');
      return true;
    }
    
    const today = new Date();
    // Obtener solo la fecha actual en formato YYYY-MM-DD para comparación precisa
    const todayString = today.toISOString().split('T')[0];
    
    // La liga está activa si la fecha de fin es HOY o POSTERIOR a hoy
    // Esto significa que la liga está activa durante todo su último día
    const isActive = endDate >= todayString;
    console.log(`Liga detalle end_date: ${endDate}, today: ${todayString}, isActive: ${isActive} (activa si end_date >= today)`);
    
    return isActive;
  };

  useEffect(() => {
    // Solo hacer fetch si el ID cambió
    if (leagueId && leagueId !== lastFetchedId.current) {
      fetchLeagueDetail(leagueId);
    }
  }, [leagueId, fetchLeagueDetail]);

  const refetch = useCallback(() => {
    if (leagueId) {
      return fetchLeagueDetail(leagueId);
    }
    return Promise.resolve();
  }, [leagueId, fetchLeagueDetail]);

  return {
    league,
    players,
    loading,
    error,
    refetch,
  };
};
